import { createApiRef, OAuthApi } from '@backstage/core-plugin-api';
import { Octokit } from '@octokit/rest';
import { GITHUB_ORG, GITHUB_REPO } from './AIProvisioning.service';

// ── Constants ─────────────────────────────────────────────────────────────────

/** Path inside GITHUB_REPO for the OneTrust approved-app-short-names policy */
export const POLICY_FILE_PATH = 'azure/policies/tags/One Trust AI Approved Shortnames/policy.json';

// ── Types ─────────────────────────────────────────────────────────────────────

/** OneTrust approved applications policy JSON structure */
export type OneTrustApprovedApps = {
  description: string;
  approvedAppShortNames: string[];
};

// ── Pure utility functions ────────────────────────────────────────────────────

/**
 * Checks whether an app short name appears in the OneTrust approved list.
 *
 * @param policy       - The fetched OneTrust policy
 * @param appShortname - The app short name to validate
 * @returns `true` if the app is approved, `false` otherwise
 */
export const validateOneTrust = (policy: OneTrustApprovedApps, appShortname: string): boolean => {
  return policy.approvedAppShortNames.includes(appShortname);
};

// ── Service interface & API ref ───────────────────────────────────────────────

/**
 * Client-side service for OneTrust AI approval validation.
 *
 * Fetches the OneTrust approved application short names policy from
 * GitHub and validates whether a given app short name has been approved.
 */
export interface AIOneTrustServiceApi {
  /** Fetches the OneTrust approved application short names policy from GitHub. */
  fetchOneTrustPolicy(): Promise<OneTrustApprovedApps>;
}

export const AIOneTrustServiceApiRef = createApiRef<AIOneTrustServiceApi>({
  id: 'plugin.devkit.ai-onetrust.service',
});

// ── Service implementation ────────────────────────────────────────────────────

/**
 * Implements {@link AIOneTrustServiceApi}.
 *
 * Constructor follows the same auth-injection pattern used by
 * `GithubEnterpriseClientGraph` in `github.service.ts` — the GitHub API URL
 * and OAuth provider are injected once, and authenticated Octokit instances
 * are created on demand.
 */
export class AIOneTrustService implements AIOneTrustServiceApi {
  constructor(
    private readonly ghecApiUrl: string,
    private readonly ghecAuthApi: OAuthApi,
  ) {}

  /** Creates an authenticated Octokit REST client. */
  private async getOctokit(): Promise<Octokit> {
    const token = await this.ghecAuthApi.getAccessToken();
    return new Octokit({ auth: token, baseUrl: this.ghecApiUrl });
  }

  async fetchOneTrustPolicy(): Promise<OneTrustApprovedApps> {
    const octokit = await this.getOctokit();
    const response = await octokit.repos.getContent({
      owner: GITHUB_ORG,
      repo: GITHUB_REPO,
      path: POLICY_FILE_PATH,
    });

    if (!('content' in response.data) || typeof response.data.content !== 'string') {
      throw new Error(`Invalid file content received from GitHub for ${POLICY_FILE_PATH}`);
    }

    const content = Buffer.from(response.data.content, 'base64').toString('utf-8');
    const parsed = JSON.parse(content) as OneTrustApprovedApps;

    if (!parsed || !Array.isArray(parsed.approvedAppShortNames)) {
      throw new Error('Policy file is empty or invalid');
    }

    return parsed;
  }
}
